import {
  AccountResource,
  ApiResource,
  DefinitionResource,
  DeprecatedApiService,
  OrganizationFeatureResource,
  OrganizationHelpers,
  OrganizationResource,
  PreferenceResource,
  ReportResource,
  ReportResourceHelper,
  ReportRunResource,
  SpaceResource,
  StarResource,
} from '@mode/legacy/data-access-model-js';
import { ofType } from '@mode/legacy/util-domain';
import { ReportFacade } from '@mode/reports/domain';
import { FeatureFlag, SpaceResource as SpaceResourceV2 } from '@mode/shared/contract-common';
import { reportRunCache } from '@mode/shared/data-access-webapp';
import { ToastService } from '@mode/shared/util-toast';
import { merge } from 'lodash';
import { Observable, merge as observableMerge } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';
import { getLinkHref } from '../../src/lib/resources/resource-helpers';
import { ClientActionType } from '../actions/client.actions';
import { EditorActionType } from '../actions/editor-actions';
import {
  accountPreferenceSelector,
  accountSelector,
  reportContextSelector,
  selectedReportResourceSelector,
} from '../reducers/selectors';
import { ReportDataService } from './report-data-service';
import { getInitialFlagValue } from '@mode/shared/util-js';

export class ClientDataService {
  public static $inject = [
    'RxStoreService',
    'actions$',
    'ReportDataService',
    'PreferenceModel',
    'ApiService',
    'ModeResource',
    'ToastService',
    'ReportFacade',
  ];
  private isExportDatasetsEnabled = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);

  constructor(
    private store: AppStore,
    private actions$: Observable<Actions>,
    private reportDataService: ReportDataService,
    private PreferenceModel,
    private api: DeprecatedApiService,
    private ModeResource: any,
    private alertService: ToastService,
    private reportFacade: ReportFacade
  ) {
    // Placeholder async like loading for client account
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadAccount),
        map((action) => action.payload.account),
        tap((account) => this.store.dispatch({ type: ClientActionType.LoadAccountSuccess, payload: { account } }))
      )
      .subscribe();

    // Placeholder async like loading for client account context
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadAccountContext),
        map((action) => action.organization),
        tap((organization) => this.store.dispatch({ type: ClientActionType.LoadAccountContextSuccess, organization }))
      )
      .subscribe();

    // Load Account Success
    this.actions$.pipe(ofType(ClientActionType.LoadAccountSuccess)).subscribe((action) => {
      if (action.payload.account.hasEmbedded('organizations')) {
        this.store.dispatch({
          type: ClientActionType.LoadAccountOrganizations,
          accountResource: action.payload.account,
        });
      }
      if (action.payload.account.hasEmbedded('preference')) {
        this.store.dispatch({ type: ClientActionType.LoadAccountPreference, accountResource: action.payload.account });
      }
    });

    // Load Account Context Success
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadAccountContextSuccess),
        map((action) => action.organization)
      )
      .subscribe((organization) => {
        if (organization.hasEmbedded('organization_features')) {
          this.store.dispatch({ type: ClientActionType.LoadAccountContextFeatures, organization });
        }
        if (organization.hasLink('spaces')) {
          this.store.dispatch({ type: ClientActionType.LoadAccountContextSpaces, organization });
        }
      });

    // Load Account Organizations
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadAccountOrganizations),
        switchMap((payload) =>
          // Load nested Organizations from Account
          this.api
            .getResources<OrganizationResource>(payload.accountResource, 'organizations')
            .then((organizations) =>
              this.store.dispatch({
                type: ClientActionType.LoadAccountOrganizationsSuccess,
                organizations: organizations,
              })
            )
            .catch((err) => this.store.dispatch({ type: ClientActionType.LoadAccountOrganizationsFailure, error: err }))
        )
      )
      .subscribe();

    // Load Account Preference
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadAccountPreference),
        switchMap((payload) =>
          // Load nested Preference from Account
          this.api
            .getResource<PreferenceResource>(payload.accountResource, 'preference')
            .then((preference) =>
              this.store.dispatch({
                type: ClientActionType.LoadAccountPreferenceSuccess,
                preferenceResource: preference,
              })
            )
            .catch((err) => this.store.dispatch({ type: ClientActionType.LoadAccountPreferenceFailure, error: err }))
        )
      )
      .subscribe();

    // Load Api
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadApi),
        map((action) => action.payload.api),
        tap((api) => this.store.dispatch({ type: ClientActionType.LoadApiSuccess, payload: { api } }))
      )
      .subscribe();

    // Load Org Context
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportContext),
        map((action) => action.organization),
        tap((organization) => this.store.dispatch({ type: ClientActionType.LoadReportContextSuccess, organization }))
      )
      .subscribe();

    // Load Org Context Success
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportContextSuccess),
        map((action) => action.organization)
      )
      .subscribe((organization) => {
        if (organization.hasEmbedded('organization_features')) {
          this.store.dispatch({ type: ClientActionType.LoadReportContextFeatures, organization });
        }
      });

    // Load Org Context Features
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportContextFeatures),
        switchMap((payload) =>
          // Load embedded Features from Organization
          this.api
            .getResource<OrganizationFeatureResource>(payload.organization, 'organization_features')
            .then((features) =>
              this.store.dispatch({ type: ClientActionType.LoadReportContextFeaturesSuccess, features })
            )
            .catch((err) =>
              this.store.dispatch({ type: ClientActionType.LoadReportContextFeaturesFailure, error: err })
            )
        )
      )
      .subscribe();

    // Load Account Context Features
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadAccountContextFeatures),
        switchMap((payload) =>
          // Load embedded Features from Organization
          this.api
            .getResource<OrganizationFeatureResource>(payload.organization, 'organization_features')
            .then((features) =>
              this.store.dispatch({ type: ClientActionType.LoadAccountContextFeaturesSuccess, features })
            )
            .catch((err) =>
              this.store.dispatch({ type: ClientActionType.LoadAccountContextFeaturesFailure, error: err })
            )
        )
      )
      .subscribe();

    // Load Account Context Spaces
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadAccountContextSpaces),
        switchMap((payload) =>
          // Load linked Spaces from Organization
          this.api
            .getResources<SpaceResource>(payload.organization, 'spaces')
            .then((spaces) => this.store.dispatch({ type: ClientActionType.LoadAccountContextSpacesSuccess, spaces }))
            .catch((err) => this.store.dispatch({ type: ClientActionType.LoadAccountContextSpacesFailure, error: err }))
        )
      )
      .subscribe();

    // Load Report Run Success
    this.actions$.pipe(ofType(ClientActionType.LoadReportRunSuccess)).subscribe((action) => {
      // Don't loaded embedded report action if the embedded report doesn't have queries, which it won't in the editor
      if (
        action.reportRunResource.hasEmbedded('report') &&
        action.reportRunResource
          .embedded('report')
          .hasEmbedded(this.isExportDatasetsEnabled ? 'local_datasets' : 'queries') // based on FF, local_datasets
      ) {
        this.reportDataService.loadEmbeddedReport(action.reportRunResource);
      }
      if (action.reportRunResource.hasEmbedded('pdf_export')) {
        this.loadReportRunEmbeddedPdfExport(action.reportRunResource);
      }
    });

    // Load Space from Url Report
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportEmbeddedSpace),
        switchMap((payload) =>
          // Load Space from Report
          this.api
            .getResource<SpaceResource>(payload.reportResource, 'space')
            .then((space) => {
              this.reportFacade.updateHeaderSpaceDataManually(space as unknown as SpaceResourceV2);
              this.store.dispatch({ type: ClientActionType.LoadReportEmbeddedSpaceSuccess, spaceResource: space });
            })
            .catch((err) => this.store.dispatch({ type: ClientActionType.LoadReportEmbeddedSpaceFailure, error: err }))
        )
      )
      .subscribe();

    // Update Preference
    observableMerge(
      this.actions$.pipe(ofType(ClientActionType.UpdatePreference)),
      this.actions$.pipe(ofType(EditorActionType.ToggleNotebookSidebar))
    )
      .pipe(
        withLatestFrom(this.store.select(accountPreferenceSelector)),
        switchMap(([action, preferenceResource]) =>
          this.updatePreference(preferenceResource)
            .then((preference) =>
              this.store.dispatch({
                type: ClientActionType.UpdatePreferenceSuccess,
                preferenceResource: preference._resource,
              })
            )
            .catch((err) => {
              alertService.error('There was an error saving your settings.');
            })
        )
      )
      .subscribe();

    // Load Report Creator from report resource link
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportCreator),
        switchMap((payload) =>
          // Load Creator from Report
          this.api
            .getResource<AccountResource>(payload.reportResource, 'creator')
            .then((creator) =>
              this.store.dispatch({ type: ClientActionType.LoadReportCreatorSuccess, creatorResource: creator })
            )
            .catch((err) => this.store.dispatch({ type: ClientActionType.LoadReportCreatorFailure, error: err }))
        )
      )
      .subscribe();

    // Load Report User Star from report resource link
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportUserStar),
        switchMap((payload) =>
          // Load Space from Report
          this.api
            .getResource<StarResource>(payload.reportResource, 'star')
            .then((star) =>
              this.store.dispatch({ type: ClientActionType.LoadReportUserStarSuccess, starResource: star })
            )
            .catch((err) => this.store.dispatch({ type: ClientActionType.LoadReportUserStarFailure, error: err }))
        )
      )
      .subscribe();

    // Current User Belongs to Report Org
    this.actions$
      .pipe(ofType(ClientActionType.LoadReportContextSuccess), withLatestFrom(this.store.select(accountSelector)))
      .subscribe(([reportContextAction, accountResource]) => {
        const { organization: orgContext } = reportContextAction;
        const homeLink = getLinkHref(orgContext, 'home_web');
        const isMember = orgContext.membership_type && orgContext.membership_type !== 'limited';
        const isOwner = reportContextAction.organization.username === accountResource?.username;

        this.store.dispatch({
          type: ClientActionType.ComputeIfUserBelongsToReportContext,
          result: isOwner || isMember,
          homeLink,
        });
      });
  }

  private updatePreference(preference: PreferenceResource): Promise<{ _resource: PreferenceResource }> {
    const model = merge(new this.PreferenceModel.model(new this.ModeResource(preference)), {
      notebook_sidebar: { items: { intro: false, shortcuts: true } },
    });

    return this.PreferenceModel.update(model);
  }

  public get accountPreference$() {
    return this.store.select(accountPreferenceSelector);
  }

  public get datasourcesEnabled$() {
    return this.store.select(reportContextSelector).pipe(map(OrganizationHelpers.canCreateDatasources));
  }

  public get filtersEnabled$() {
    return this.store.select(selectedReportResourceSelector).pipe(map(ReportResourceHelper.hasFiltersLink));
  }

  public setLoggedInState(loggedIn: boolean) {
    this.store.dispatch({
      type: ClientActionType.LoadLoggedInState,
      loggedIn,
    });
  }

  public loadAccount(account: AccountResource) {
    this.store.dispatch({
      type: ClientActionType.LoadAccount,
      payload: {
        account,
      },
    });
  }

  public loadAccountContext(organization: OrganizationResource) {
    this.store.dispatch({
      type: ClientActionType.LoadAccountContext,
      organization,
    });
  }

  public loadApi(api: ApiResource) {
    this.store.dispatch({
      type: ClientActionType.LoadApi,
      payload: {
        api,
      },
    });
  }

  public loadReportOwner(organization: OrganizationResource) {
    this.store.dispatch({
      type: ClientActionType.LoadReportContext,
      organization,
    });
  }

  public loadReportRunSuccess(reportRun: ReportRunResource) {
    // hack to support data-access functions for children of report run resource
    reportRunCache.set(reportRun.token, reportRun as any);
    this.store.dispatch({
      type: ClientActionType.LoadReportRunSuccess,
      reportRunResource: reportRun,
    });
  }

  public loadReportSuccess(report: ReportResource, isNew: boolean, isDataset: boolean) {
    this.store.dispatch({
      type: ClientActionType.LoadReportSuccess,
      reportResource: report,
      isNew: isNew,
      isDataset,
      isEmbedded: false,
    });
  }

  public loadDefinitionSuccess(definition: DefinitionResource) {
    this.store.dispatch({
      type: ClientActionType.LoadDefinitionSuccess,
      definitionResource: definition,
    });
  }

  /**
   * @todo move to ReportRunDataservice once available
   * @param {ReportRunResource} resource
   */
  public loadReportRunEmbeddedPdfExport(resource: ReportRunResource) {
    this.store.dispatch({
      type: ClientActionType.LoadReportRunEmbeddedPdfExport,
      reportRunResource: resource,
    });
  }

  public updateAccountPreference(params: any) {
    this.store.dispatch({
      type: ClientActionType.UpdatePreference,
      params: params,
    });
  }
}
